import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  CheckCircle,
  Block,
  Link as LinkIcon,
  Download,
  LockReset,
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const Students = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [batches, setBatches] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedBatchForApproval, setSelectedBatchForApproval] = useState('');
  const [studentToApprove, setStudentToApprove] = useState(null);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    student_name: '',
    parent_name: '',
    phone: '',
    alternate_phone: '',
    year_of_joining: new Date().getFullYear(),
    month_of_joining: new Date().getMonth() + 1,
    batch_id: '',
    is_approved: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [resetLinkDialogOpen, setResetLinkDialogOpen] = useState(false);
  const [resetLink, setResetLink] = useState('');

  useEffect(() => {
    fetchStudents();
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, statusFilter, batchFilter]);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        search,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        batch_id: batchFilter !== 'all' ? batchFilter : undefined,
      };
      const response = await axios.get('/api/admin/students', { 
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/batches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data.batches);
    } catch (error) {
      console.error('Failed to load batches');
    }
  };

  const handleApproveStudent = (studentId) => {
    // Open dialog to select batch before approving
    handleMenuClose(); // Close menu first
    setStudentToApprove(studentId); // Store student ID for approval
    setSelectedBatchForApproval('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalDialogClose = () => {
    setApprovalDialogOpen(false);
    setSelectedBatchForApproval('');
    setStudentToApprove(null);
  };

  const handleConfirmApproval = async () => {
    if (!selectedBatchForApproval) {
      toast.error('Please select a batch');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/admin/students/${studentToApprove}/approve`, {
        batch_id: selectedBatchForApproval
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student approved and assigned to batch');
      handleApprovalDialogClose();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve student');
    }
  };

  const handleDeactivateStudent = async (studentId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/admin/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student deactivated successfully');
      fetchStudents();
    } catch (error) {
      toast.error('Failed to deactivate student');
    }
    handleMenuClose();
  };

  const handleCreateInvite = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/admin/students/create-invite', {
        student_name: 'New Student',
        parent_name: 'Parent Name',
        phone: '1234567890',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const link = `${window.location.origin}/register?invite=${response.data.invite_token}`;
      setInviteLink(link);
      setInviteDialogOpen(true);
      toast.success('Invite link created successfully');
    } catch (error) {
      toast.error('Failed to create invite link');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/export/students', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Convert to CSV and download
      const csv = response.data.data.map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'students.csv';
      a.click();
      toast.success('Students exported successfully');
    } catch (error) {
      toast.error('Failed to export students');
    }
  };

  const handleMenuOpen = (event, student) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudent(student);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStudent(null);
  };

  const handleAddDialogOpen = () => {
    setAddDialogOpen(true);
    setFormData({
      email: '',
      password: '',
      student_name: '',
      parent_name: '',
      phone: '',
      alternate_phone: '',
      year_of_joining: new Date().getFullYear(),
      month_of_joining: new Date().getMonth() + 1,
      batch_id: '',
      is_approved: true,
    });
    setFormErrors({});
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setFormData({
      email: '',
      password: '',
      student_name: '',
      parent_name: '',
      phone: '',
      alternate_phone: '',
      year_of_joining: new Date().getFullYear(),
      month_of_joining: new Date().getMonth() + 1,
      batch_id: '',
      is_approved: true,
    });
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // If batch is changed, also update the monthly_fee
    if (name === 'batch_id') {
      const selectedBatch = batches.find(b => Number(b.id) === Number(value));
      const newMonthlyFee = selectedBatch?.monthly_fee || 0;
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        monthly_fee: newMonthlyFee,
        // Reset concession if it exceeds the new batch fee
        concession_amount: Math.min(prev.concession_amount || 0, newMonthlyFee)
      }));
    } else if (name === 'concession_amount') {
      // Ensure concession is not negative and not more than batch fee
      const numValue = Math.max(0, Math.min(Number(value) || 0, formData.monthly_fee || 0));
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (!formData.student_name) errors.student_name = 'Student name is required';
    if (!formData.parent_name) errors.parent_name = 'Parent name is required';
    if (!formData.phone) errors.phone = 'Phone is required';
    else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) errors.phone = 'Invalid phone format';
    if (!formData.year_of_joining) errors.year_of_joining = 'Year of joining is required';
    if (!formData.month_of_joining) errors.month_of_joining = 'Month of joining is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddStudent = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/admin/students', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student added successfully');
      handleAddDialogClose();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add student');
    }
  };

  const handleEditDialogOpen = (student) => {
    setStudentToEdit(student);
    setFormData({
      email: student.email,
      password: '', // Don't populate password for security
      student_name: student.student_name,
      parent_name: student.parent_name || '',
      phone: student.phone || '',
      alternate_phone: student.alternate_phone || '',
      year_of_joining: student.year_of_joining || new Date().getFullYear(),
      month_of_joining: student.month_of_joining || new Date().getMonth() + 1,
      batch_id: student.batch_id || '',
      concession_amount: student.concession_amount || 0,
      monthly_fee: student.monthly_fee || 0,  // Store the actual monthly fee from backend
      is_approved: student.is_approved,
      is_active: student.is_active,
    });
    setFormErrors({});
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setStudentToEdit(null);
    setFormData({
      email: '',
      password: '',
      student_name: '',
      parent_name: '',
      phone: '',
      alternate_phone: '',
      year_of_joining: new Date().getFullYear(),
      month_of_joining: new Date().getMonth() + 1,
      batch_id: '',
      concession_amount: 0,
      is_approved: true,
    });
    setFormErrors({});
  };

  const validateEditForm = () => {
    const errors = {};
    if (!formData.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    // Password is optional for edit (only validate if provided)
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!formData.student_name) errors.student_name = 'Student name is required';
    if (!formData.parent_name) errors.parent_name = 'Parent name is required';
    if (!formData.phone) errors.phone = 'Phone is required';
    else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) errors.phone = 'Invalid phone format';
    if (!formData.year_of_joining) errors.year_of_joining = 'Year of joining is required';
    if (!formData.month_of_joining) errors.month_of_joining = 'Month of joining is required';
    
    // Validate concession amount
    const concession = Number(formData.concession_amount) || 0;
    const monthlyFee = Number(formData.monthly_fee) || 0;
    if (concession < 0) errors.concession_amount = 'Concession cannot be negative';
    else if (concession > monthlyFee) errors.concession_amount = 'Concession cannot exceed batch fee';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateStudent = async () => {
    if (!validateEditForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      // Only include password in update if it was changed
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const token = localStorage.getItem('access_token');
      await axios.put(`/api/admin/students/${studentToEdit.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student updated successfully');
      handleEditDialogClose();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update student');
    }
  };

  const handleGenerateResetLink = async (student) => {
    handleMenuClose();
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `/api/admin/students/${student.id}/reset-password-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const link = `${window.location.origin}/reset-password?token=${response.data.token}`;
      setResetLink(link);
      setResetLinkDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate reset link');
    }
  };

  const getStatusChip = (student) => {
    if (!student.is_approved) {
      return <Chip label="Pending" color="warning" size="small" />;
    }
    if (!student.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3 
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Students
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage student registrations and profiles
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={handleCreateInvite}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create Invite</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Invite</Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddDialogOpen}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            >
              Add Student
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search students..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Batch"
                  value={batchFilter}
                  onChange={(e) => setBatchFilter(e.target.value)}
                >
                  <MenuItem value="all">All Batches</MenuItem>
                  {batches.map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      {batch.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Students Table/Card View */}
        <Card>
          {isMobile ? (
            // Mobile Card View
            <Box sx={{ p: 2 }}>
              {students.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No students found
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {students.map((student) => (
                    <Card 
                      key={student.id} 
                      sx={{ 
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                        border: '1px solid',
                        borderColor: 'rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                          transform: 'translateY(-2px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                        },
                      }}
                    >
                      <CardContent sx={{ pl: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                                background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                                width: 48, 
                                height: 48,
                                fontWeight: 600,
                                fontSize: '1.2rem',
                                boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
                              }}
                            >
                              {student.student_name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="subtitle1" 
                                fontWeight={700}
                                sx={{ 
                                  color: '#2C2416',
                                  fontSize: '1.1rem',
                                }}
                                noWrap
                              >
                                {student.student_name}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#8B7355',
                                  fontSize: '0.8rem',
                                }}
                                noWrap
                              >
                                {student.email}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, student)}
                            sx={{ 
                              ml: 1,
                              color: '#D4AF37',
                              '&:hover': {
                                bgcolor: 'rgba(212, 175, 55, 0.1)',
                              },
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                        
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                        
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#8B7355',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '0.7rem',
                              }}
                              display="block"
                            >
                              Parent Name
                            </Typography>
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              sx={{ color: '#2C2416', mt: 0.3 }}
                            >
                              {student.parent_name}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#8B7355',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontSize: '0.7rem',
                            }}
                            display="block"
                          >
                            Contact
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{ color: '#2C2416', mt: 0.3 }}
                          >
                            {student.phone}
                          </Typography>
                          {student.alternate_phone && (
                            <Typography 
                              variant="caption" 
                              sx={{ color: '#8B7355', fontSize: '0.75rem' }}
                            >
                              {student.alternate_phone}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: 120 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#8B7355',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '0.7rem',
                              }}
                              display="block"
                            >
                              Batch
                            </Typography>
                            {student.batch_name ? (
                              <Chip 
                                label={student.batch_name} 
                                size="small" 
                                sx={{ 
                                  mt: 0.5,
                                  bgcolor: 'rgba(212, 175, 55, 0.1)',
                                  color: '#8B4513',
                                  borderColor: 'rgba(212, 175, 55, 0.3)',
                                  fontWeight: 600,
                                }} 
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: '#8B7355', mt: 0.3 }}>
                                Not assigned
                              </Typography>
                            )}
                          </Box>
                          
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#8B7355',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '0.7rem',
                              }}
                              display="block"
                            >
                              Year
                            </Typography>
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              sx={{ color: '#2C2416', mt: 0.3 }}
                            >
                              {student.year_of_joining}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#8B7355',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontSize: '0.7rem',
                              mb: 0.5,
                            }}
                            display="block"
                          >
                            Status
                          </Typography>
                          {getStatusChip(student)}
                        </Box>
                      </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Parent Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Year of Joining</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {student.student_name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {student.student_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{student.parent_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{student.phone}</Typography>
                        {student.alternate_phone && (
                          <Typography variant="caption" color="text.secondary">
                            {student.alternate_phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {student.batch_name ? (
                          <Chip label={student.batch_name} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{student.year_of_joining}</TableCell>
                      <TableCell>{getStatusChip(student)}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={(e) => handleMenuOpen(e, student)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
          />
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedStudent && !selectedStudent.is_approved && (
            <MenuItem onClick={() => handleApproveStudent(selectedStudent.id)}>
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Approve Student</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleEditDialogOpen(selectedStudent)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDeactivateStudent(selectedStudent?.id)}>
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleGenerateResetLink(selectedStudent)}>
            <ListItemIcon>
              <LockReset fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset Password Link</ListItemText>
          </MenuItem>
        </Menu>

        {/* Invite Dialog */}
        <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Invite Link Created</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share this link with the student to complete registration:
            </Typography>
            <TextField
              fullWidth
              value={inviteLink}
              margin="normal"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(inviteLink);
                        toast.success('Link copied to clipboard');
                      }}
                    >
                      Copy
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Add Student Dialog */}
        <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Add New Student</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Login Credentials */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Login Credentials
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    required
                  />
                </Grid>

                {/* Student Information */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Student Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Student Name"
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleFormChange}
                    error={!!formErrors.student_name}
                    helperText={formErrors.student_name}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Parent/Guardian Name"
                    name="parent_name"
                    value={formData.parent_name}
                    onChange={handleFormChange}
                    error={!!formErrors.parent_name}
                    helperText={formErrors.parent_name}
                    required
                  />
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alternate Phone (Optional)"
                    name="alternate_phone"
                    value={formData.alternate_phone}
                    onChange={handleFormChange}
                  />
                </Grid>

                {/* Batch & Year */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Batch & Year
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Batch"
                    name="batch_id"
                    value={formData.batch_id}
                    onChange={handleFormChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {batches.map((batch) => (
                      <MenuItem key={batch.id} value={batch.id}>
                        {batch.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Month of Joining"
                    name="month_of_joining"
                    value={formData.month_of_joining}
                    onChange={handleFormChange}
                    error={!!formErrors.month_of_joining}
                    helperText={formErrors.month_of_joining}
                    required
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Year of Joining"
                    name="year_of_joining"
                    type="number"
                    value={formData.year_of_joining}
                    onChange={handleFormChange}
                    error={!!formErrors.year_of_joining}
                    helperText={formErrors.year_of_joining}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleAddDialogClose}>Cancel</Button>
            <Button variant="contained" onClick={handleAddStudent}>
              Add Student
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Student Dialog */}
        <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Student</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Login Credentials */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Login Credentials
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password || 'Leave blank to keep current password'}
                  />
                </Grid>

                {/* Student Information */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Student Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Student Name"
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleFormChange}
                    error={!!formErrors.student_name}
                    helperText={formErrors.student_name}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Parent/Guardian Name"
                    name="parent_name"
                    value={formData.parent_name}
                    onChange={handleFormChange}
                    error={!!formErrors.parent_name}
                    helperText={formErrors.parent_name}
                    required
                  />
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alternate Phone (Optional)"
                    name="alternate_phone"
                    value={formData.alternate_phone}
                    onChange={handleFormChange}
                  />
                </Grid>

                {/* Batch & Year */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Batch & Year
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Batch"
                    name="batch_id"
                    value={formData.batch_id}
                    onChange={handleFormChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {batches.map((batch) => (
                      <MenuItem key={batch.id} value={batch.id}>
                        {batch.name} (₹{batch.monthly_fee}/month)
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Month of Joining"
                    name="month_of_joining"
                    value={formData.month_of_joining}
                    onChange={handleFormChange}
                    error={!!formErrors.month_of_joining}
                    helperText={formErrors.month_of_joining}
                    required
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Year of Joining"
                    name="year_of_joining"
                    type="number"
                    value={formData.year_of_joining}
                    onChange={handleFormChange}
                    error={!!formErrors.year_of_joining}
                    helperText={formErrors.year_of_joining}
                    required
                  />
                </Grid>

                {/* Fee Concession */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Fee Concession
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Monthly Concession Amount"
                    name="concession_amount"
                    type="number"
                    value={formData.concession_amount}
                    onChange={handleFormChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    helperText="Amount to deduct from monthly fee"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {formData.batch_id && (
                    <Box sx={{ p: 2, bgcolor: 'rgba(46, 125, 50, 0.1)', borderRadius: 1, border: '1px solid rgba(46, 125, 50, 0.3)' }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Batch Fee:</strong> ₹{formData.monthly_fee || 0}
                      </Typography>
                      <Typography variant="body2" color="error.main">
                        <strong>Concession:</strong> -₹{formData.concession_amount || 0}
                      </Typography>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="body1" color="success.main" fontWeight={700}>
                        <strong>Effective Fee:</strong> ₹{Math.max(0, (formData.monthly_fee || 0) - (formData.concession_amount || 0))}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleEditDialogClose}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateStudent}>
              Update Student
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Link Dialog */}
        <Dialog open={resetLinkDialogOpen} onClose={() => setResetLinkDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Password Reset Link</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share this link with the student via WhatsApp or phone. It expires in <strong>24 hours</strong>.
            </Typography>
            <TextField
              fullWidth
              value={resetLink}
              margin="normal"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(resetLink);
                        toast.success('Link copied to clipboard');
                      }}
                    >
                      Copy
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetLinkDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog with Batch Selection */}
        <Dialog 
          open={approvalDialogOpen} 
          onClose={handleApprovalDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Approve Student & Assign Batch</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please select a batch to assign this student before approval.
              </Typography>
              <TextField
                fullWidth
                select
                label="Select Batch"
                value={selectedBatchForApproval}
                onChange={(e) => setSelectedBatchForApproval(e.target.value)}
                sx={{ mt: 2 }}
                required
              >
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleApprovalDialogClose}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleConfirmApproval}
              disabled={!selectedBatchForApproval}
            >
              Approve & Assign
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default Students;
